import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { customersAPI, extractErrorMessage } from '../api';
import { useToast } from '../components/Toast';

const Customers = () => {
  const { selectedStore } = useStore();
  const showToast = useToast();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '', whatsapp_phone: '', instagram_handle: '', address: '', notes: '',
  });

  // Helper functions
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const renderIcon = (iconName) => {
    const icons = {
      users: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      userPlus: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="8.5" cy="7" r="4" />
          <line x1="20" y1="8" x2="20" y2="14" />
          <line x1="23" y1="11" x2="17" y2="11" />
        </svg>
      ),
      userCheck: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="8.5" cy="7" r="4" />
          <polyline points="17 11 19 13 23 9" />
        </svg>
      ),
      edit: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      ),
      trash: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      ),
      userCircle: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
    };
    return icons[iconName] || null;
  };

  // Calculate stats
  const stats = {
    total: customers.length,
    activeThisMonth: customers.filter(c => {
      if (!c.created_at) return false;
      const createdDate = new Date(c.created_at);
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      return createdDate >= monthAgo;
    }).length,
    newThisWeek: customers.filter(c => {
      if (!c.created_at) return false;
      const createdDate = new Date(c.created_at);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return createdDate >= weekAgo;
    }).length,
    withInstagram: customers.filter(c => c.instagram_handle).length,
  };

  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      const response = await customersAPI.list(selectedStore.id, params);
      setCustomers(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load customers'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore, search]);

  useEffect(() => {
    if (selectedStore) loadCustomers();
  }, [selectedStore, loadCustomers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, store_id: selectedStore.id };
      if (editingId) {
        await customersAPI.update(editingId, data);
        showToast('Customer updated successfully', 'success');
      } else {
        await customersAPI.create(data);
        showToast('Customer added successfully', 'success');
      }
      loadCustomers();
      resetForm();
    } catch (err) {
      setError(extractErrorMessage(err, 'Operation failed'));
    }
  };

  const handleEdit = (customer) => {
    setFormData({
      name: customer.name || '',
      whatsapp_phone: customer.whatsapp_phone || '',
      instagram_handle: customer.instagram_handle || '',
      address: customer.address || '',
      notes: customer.notes || '',
    });
    setEditingId(customer.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await customersAPI.delete(id);
      showToast('Customer deleted', 'success');
      loadCustomers();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to delete customer'));
    }
  };

  const resetForm = () => {
    setFormData({ name: '', whatsapp_phone: '', instagram_handle: '', address: '', notes: '' });
    setEditingId(null);
    setShowForm(false);
    setError(null);
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div><span>Loading customers...</span></div>;
  }

  return (
    <div className="page-modern">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Customers</h1>
          <p className="page-subtitle">Manage your customer relationships and contact information</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }}
        >
          {showForm ? 'Cancel' : '+ Add Customer'}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Stats Row */}
      <div className="page-stats-row">
        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--primary)' }}>
            {renderIcon('users')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Total Customers</div>
            <div className="page-stat-value">{formatNumber(stats.total)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--success)' }}>
            {renderIcon('userCheck')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Active This Month</div>
            <div className="page-stat-value">{formatNumber(stats.activeThisMonth)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--warning)' }}>
            {renderIcon('userPlus')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">New This Week</div>
            <div className="page-stat-value">{formatNumber(stats.newThisWeek)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: '#8B5CF6' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
            </svg>
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">With Instagram</div>
            <div className="page-stat-value">{formatNumber(stats.withInstagram)}</div>
          </div>
        </div>
      </div>

      {/* Filter Card */}
      <div className="card page-filter-card">
        <div className="page-filter-grid">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Search Customers</label>
            <input
              type="text"
              className="form-input"
              placeholder="Search by name, phone, or Instagram..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button className="btn btn-secondary" onClick={loadCustomers} style={{ alignSelf: 'flex-end' }}>
            Apply Filters
          </button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">{editingId ? 'Edit Customer' : 'Add New Customer'}</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp Phone *</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.whatsapp_phone}
                    onChange={(e) => setFormData({ ...formData, whatsapp_phone: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Instagram Handle</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="@username"
                    value={formData.instagram_handle}
                    onChange={(e) => setFormData({ ...formData, instagram_handle: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-textarea"
                  value={formData.notes}
                  rows={3}
                  placeholder="Additional notes about this customer..."
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Update Customer' : 'Create Customer'}
                </button>
                {editingId && (
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Data Table Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">All Customers ({customers.length})</h3>
          <div className="card-header-actions">
            <button className="btn btn-sm btn-secondary">Export</button>
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {customers.length === 0 ? (
            <div className="page-empty-state">
              <div className="page-empty-icon">
                {renderIcon('userCircle')}
              </div>
              <div className="page-empty-title">No customers yet</div>
              <p className="page-empty-text">
                Your customer list will appear here once you add your first customer or make a sale
              </p>
              <button
                className="btn btn-primary"
                style={{ marginTop: '1.5rem' }}
                onClick={() => setShowForm(true)}
              >
                Add Your First Customer
              </button>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="table-container hidden-mobile">
                <table>
                  <thead>
                    <tr>
                      <th>Customer Name</th>
                      <th>WhatsApp</th>
                      <th>Instagram</th>
                      <th>Address</th>
                      <th>Created</th>
                      <th style={{ width: '100px', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map(customer => (
                      <tr key={customer.id}>
                        <td className="font-medium">{customer.name}</td>
                        <td>{customer.whatsapp_phone || '-'}</td>
                        <td>{customer.instagram_handle || '-'}</td>
                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {customer.address || '-'}
                        </td>
                        <td>{formatDate(customer.created_at)}</td>
                        <td>
                          <div className="table-actions">
                            <button
                              className="table-action-btn"
                              onClick={() => handleEdit(customer)}
                              title="Edit customer"
                            >
                              {renderIcon('edit')}
                            </button>
                            <button
                              className="table-action-btn table-action-btn-danger"
                              onClick={() => handleDelete(customer.id)}
                              title="Delete customer"
                            >
                              {renderIcon('trash')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards hidden-desktop">
                {customers.map(customer => (
                  <div className="mobile-card" key={customer.id}>
                    <div className="mobile-card-header">
                      <div className="mobile-card-title">{customer.name}</div>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">WhatsApp</span>
                      <span>{customer.whatsapp_phone || '-'}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Instagram</span>
                      <span>{customer.instagram_handle || '-'}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Address</span>
                      <span>{customer.address || '-'}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Created</span>
                      <span>{formatDate(customer.created_at)}</span>
                    </div>
                    <div className="mobile-card-actions">
                      <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(customer)}>
                        Edit
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(customer.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Customers;
